import * as React from "react";
import { cn } from "@/lib/utils";

export interface ScrollShadowProps extends React.HTMLAttributes<HTMLDivElement> {
  hideScrollBar?: boolean;
  size?: number;
}

const ScrollShadow = React.forwardRef<HTMLDivElement, ScrollShadowProps>(
  (
    { className, hideScrollBar = false, size = 24, children, ...props },
    ref
  ) => {
    const [showTopShadow, setShowTopShadow] = React.useState(false);
    const [showBottomShadow, setShowBottomShadow] = React.useState(false);
    const [backgroundColor, setBackgroundColor] =
      React.useState<string>("rgb(255, 255, 255)");
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Combine refs - return the scrollable element
    React.useImperativeHandle(ref, () => scrollRef.current as HTMLDivElement);

    const checkScroll = React.useCallback(() => {
      const element = scrollRef.current;
      if (!element) return;

      const { scrollTop, scrollHeight, clientHeight } = element;

      // Show top shadow if scrolled down
      setShowTopShadow(scrollTop > 0);

      // Show bottom shadow if there's more content below
      setShowBottomShadow(scrollTop + clientHeight < scrollHeight - 1);
    }, []);

    // Detect background color from parent
    React.useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const computedStyle = window.getComputedStyle(container);
      const bgColor = computedStyle.backgroundColor || "rgb(255, 255, 255)";
      setBackgroundColor(bgColor);
    }, []);

    React.useEffect(() => {
      const element = scrollRef.current;
      if (!element) return;

      // Initial check
      checkScroll();

      // Add scroll listener
      element.addEventListener("scroll", checkScroll, { passive: true });

      // Add resize observer to handle content changes
      const resizeObserver = new ResizeObserver(() => {
        checkScroll();
      });

      resizeObserver.observe(element);

      // Also observe children changes
      if (element.firstElementChild) {
        resizeObserver.observe(element.firstElementChild);
      }

      return () => {
        element.removeEventListener("scroll", checkScroll);
        resizeObserver.disconnect();
      };
    }, [checkScroll]);

    // Convert rgb/rgba to rgba with opacity
    const getRgbaColor = (color: string, opacity: number): string => {
      if (color.startsWith("rgb")) {
        const rgbMatch = color.match(/\d+/g);
        if (rgbMatch && rgbMatch.length >= 3) {
          return `rgba(${rgbMatch[0]}, ${rgbMatch[1]}, ${rgbMatch[2]}, ${opacity})`;
        }
      }
      return color;
    };

    return (
      <div className="relative" ref={containerRef}>
        {/* Top shadow - positioned absolutely over the scrollable content */}
        {showTopShadow && (
          <div
            className="absolute top-0 left-0 right-0 z-10 pointer-events-none"
            style={{
              height: `${size}px`,
              background: `linear-gradient(to bottom, ${getRgbaColor(backgroundColor, 0.12)} 0%, ${getRgbaColor(backgroundColor, 0.06)} 50%, transparent 100%)`,
              maskImage: `radial-gradient(ellipse 80% 50% at 50% 0%, black 40%, transparent 70%)`,
              WebkitMaskImage: `radial-gradient(ellipse 80% 50% at 50% 0%, black 40%, transparent 70%)`,
            }}
          />
        )}

        <div
          ref={scrollRef}
          className={cn(
            "overflow-auto",
            hideScrollBar && "no-scrollbar",
            className
          )}
          {...props}
        >
          {children}
        </div>

        {/* Bottom shadow - positioned absolutely over the scrollable content */}
        {showBottomShadow && (
          <div
            className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none"
            style={{
              height: `${size}px`,
              background: `linear-gradient(to top, ${getRgbaColor(backgroundColor, 0.12)} 0%, ${getRgbaColor(backgroundColor, 0.06)} 50%, transparent 100%)`,
              maskImage: `radial-gradient(ellipse 80% 50% at 50% 100%, black 40%, transparent 70%)`,
              WebkitMaskImage: `radial-gradient(ellipse 80% 50% at 50% 100%, black 40%, transparent 70%)`,
            }}
          />
        )}
      </div>
    );
  }
);
ScrollShadow.displayName = "ScrollShadow";

export { ScrollShadow };
